import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { SearchState } from '@nx-ahs/search';

/**
 * Formly field type that renders an inline search panel (criteria + results)
 * using the shared SearchComponent. Unlike `formly-person-search`, there is no
 * modal — the search UI is embedded directly in the form.
 *
 * Props:
 *   searchId              — (required) ID of the search definition to load
 *   criteriaValues        — initial criteria to pre-populate (default: {})
 *   allowSingleSelect     — highlight a row on click (default: true)
 *   searchOnLoad          — run the search automatically when the component loads (default: false)
 *   showCriteria          — show the criteria panel (default: true)
 *   criteriaExpandedOnLoad — expand criteria panel on load (default: true)
 *   canCustomizeView      — show the column-customisation button (default: false)
 *   canExport             — show the export button (default: false)
 *   hidePagination        — hide the paginator (default: false)
 *   pageSize              — initial page size (default: 50)
 *
 * Model value:
 *   The full row object from the search result is written to formControl on
 *   row click. Downstream fields can read individual properties via
 *   `model.<key>.<property>` in hideExpressions or defaultValues.
 */
@Component({
	selector: 'app-mat-formly-inline-search',
	templateUrl: './mat-formly-inline-search.component.html',
	styleUrl: './mat-formly-inline-search.component.css',
	standalone: false
})
export class MatFormlyInlineSearchComponent extends FieldType<FieldTypeConfig> implements OnInit {
	public searchId: number;
	public criteriaValues: Record<string, unknown> = {};
	public allowSingleSelect: boolean = true;
	public searchOnLoad: boolean = false;
	public showCriteria: boolean = true;
	public criteriaExpandedOnLoad: boolean = true;
	public canCustomizeView: boolean = false;
	public canExport: boolean = false;
	public hidePagination: boolean = false;
	public searchState: SearchState;

	constructor(private cdr: ChangeDetectorRef) {
		super();
	}

	ngOnInit(): void {
		this.searchId = this.props['searchId'];
		this.criteriaValues = this.props['criteriaValues'] ?? {};
		this.allowSingleSelect = this.props['allowSingleSelect'] ?? true;
		this.searchOnLoad = this.props['searchOnLoad'] ?? false;
		this.showCriteria = this.props['showCriteria'] ?? true;
		this.criteriaExpandedOnLoad = this.props['criteriaExpandedOnLoad'] ?? true;
		this.canCustomizeView = this.props['canCustomizeView'] ?? false;
		this.canExport = this.props['canExport'] ?? false;
		this.hidePagination = this.props['hidePagination'] ?? false;
		this.searchState = new SearchState(this.props['pageSize']);
	}

	/**
	 * formly-form / formly-group are OnPush, and SearchComponent loads its definition
	 * and results asynchronously without marking any ancestor dirty. Without this, the
	 * criteria form and results don't render until some unrelated event re-checks the
	 * view. Wired to SearchComponent's criteriaFormInitialized and searchFinished outputs.
	 */
	public onSearchStateChanged(): void {
		this.cdr.markForCheck();
	}

	public onRowClicked(row: unknown): void {
		this.formControl.setValue(row);
		this.formControl.markAsTouched();
	}
}
